import { z } from 'zod';
import { HOME_PLACE, NeedEffects, TRAVEL_MODE_KEYS } from './play';
import { PersonView } from './people';
import { OwnedThing, RoomShape } from './home';

// Visits, as in Lagos Life: drop by a neighbour's home when they are in. You travel there, knock, and they let
// you in (or not); a friend they invited over walks straight in. Inside you see their home in 3D with them in
// it, and you can do things together. They can say it is time to go; if they head out, you go home too.

// where a player is during a visit: at the door (knocking) or inside, at a home on the map's "home" place
export const DOOR_PREFIX = 'door:';
export const VISIT_PREFIX = 'visit:';
export const doorOf = (hostLifeId: string) => `${DOOR_PREFIX}${hostLifeId}`;
export const visitOf = (hostLifeId: string) => `${VISIT_PREFIX}${hostLifeId}`;
// whose home a visiting place is (null for any other place)
export function hostOf(placeId: string | null | undefined): { hostLifeId: string; inside: boolean } | null {
  if (!placeId) return null;
  if (placeId.startsWith(VISIT_PREFIX)) return { hostLifeId: placeId.slice(VISIT_PREFIX.length), inside: true };
  if (placeId.startsWith(DOOR_PREFIX)) return { hostLifeId: placeId.slice(DOOR_PREFIX.length), inside: false };
  return null;
}
// the place on the map: a home visit is at the homes (the "home" place)
export const mapPlaceOf = (placeId: string) => (hostOf(placeId) ? HOME_PLACE : placeId);
// the room a player is in, for "who is here": your own home is a room of its own (with your guests in it)
export const roomOf = (lifeId: string, placeId: string) => (placeId === HOME_PLACE ? visitOf(lifeId) : placeId);

// the rules the admins keep (content, published with the rest): how long a knock waits, how soon you may knock
// again after no answer, how long an invite lasts, how many guests at once, and what a visit does for both
const effects = NeedEffects;
export const VisitRules = z.object({
  knockWaitSeconds: z.number().int().min(15).max(900),
  knockAgainMinutes: z.number().int().min(0).max(1_440),
  inviteHours: z.number().int().min(1).max(168),
  maxGuests: z.number().int().min(1).max(20),
  // when the guest comes in: what it does for the guest, and for the host
  welcome: z.object({ guest: effects, host: effects }),
});
export type VisitRules = z.infer<typeof VisitRules>;
export const DEFAULT_VISIT_RULES: VisitRules = {
  knockWaitSeconds: 120,
  knockAgainMinutes: 10,
  inviteHours: 24,
  maxGuests: 6,
  welcome: { guest: { social: 12, fun: 5 }, host: { social: 10 } },
};

export const VisitStatus = z.enum(['knocking', 'in', 'ended']);
export type VisitStatus = z.infer<typeof VisitStatus>;
// how a visit ended: the guest left (or walked away from the door), the host turned them away or said it was
// time to go, the host headed out, or nobody came to the door
export const VisitEnd = z.enum(['left', 'walked_away', 'turned_away', 'sent_home', 'host_out', 'no_answer']);
export type VisitEnd = z.infer<typeof VisitEnd>;

const look = PersonView.shape.look;
// a home on the Neighbours list, or the home you are visiting
export const NeighbourView = z.object({
  lifeId: z.string().uuid(),
  firstName: z.string(),
  username: z.string().nullable(),
  look,
  home: z.object({ type: z.string(), label: z.string(), area: z.string(), room: RoomShape }),
  // things placed in it
  items: z.number().int().min(0),
  atHome: z.boolean(),
  online: z.boolean(),
  // they invited you over and it still stands: walk straight in
  invited: z.boolean(),
});
export type NeighbourView = z.infer<typeof NeighbourView>;
export const NeighboursReply = z.object({ neighbours: z.array(NeighbourView) });
export type NeighboursReply = z.infer<typeof NeighboursReply>;

// someone at your door or in your home (you are the host)
export const GuestView = z.object({
  visitId: z.string().uuid(),
  lifeId: z.string().uuid(),
  firstName: z.string(),
  username: z.string().nullable(),
  look,
  status: z.enum(['knocking', 'in']),
  // still on the way (the knock comes when they arrive)
  arrivesAt: z.string(),
});
export type GuestView = z.infer<typeof GuestView>;

// your visit to someone (you are the guest)
export const MyVisitView = z.object({
  id: z.string().uuid(),
  status: VisitStatus,
  ended: VisitEnd.nullable(),
  invited: z.boolean(),
  host: NeighbourView,
  arrivesAt: z.string(),
  // a knock not answered by then ends the visit
  answerBy: z.string().nullable(),
});
export type MyVisitView = z.infer<typeof MyVisitView>;

export const VisitsReply = z.object({
  // the visit you are on, or the last one just ended (so the app can say why)
  visit: MyVisitView.nullable(),
  // at your door and in your home
  guests: z.array(GuestView),
});
export type VisitsReply = z.infer<typeof VisitsReply>;

// inside: their home as it stands, and everyone in it
export const VisitHomeReply = z.object({
  host: NeighbourView,
  things: z.array(OwnedThing),
  guests: z.array(GuestView),
});
export type VisitHomeReply = z.infer<typeof VisitHomeReply>;

export const StartVisitRequest = z
  .object({
    hostLifeId: z.string().uuid(),
    mode: z.enum(TRAVEL_MODE_KEYS),
  })
  .strict();
export type StartVisitRequest = z.infer<typeof StartVisitRequest>;
export const AnswerKnockRequest = z.object({ letIn: z.boolean() }).strict();
export type AnswerKnockRequest = z.infer<typeof AnswerKnockRequest>;
export const InviteOverRequest = z.object({ lifeId: z.string().uuid() }).strict();
export type InviteOverRequest = z.infer<typeof InviteOverRequest>;

// the invite's card in a chat
export const ChatInvite = z.object({
  id: z.string().uuid(),
  hostLifeId: z.string().uuid(),
  homeLabel: z.string(),
  area: z.string(),
  expiresAt: z.string(),
  // open (can walk in), used (they came), or past its time
  status: z.enum(['open', 'used', 'expired']),
});
export type ChatInvite = z.infer<typeof ChatInvite>;

// what the app says when a visit ends, from the guest's side
export const VISIT_END_TEXT: Record<VisitEnd, (host: string) => string> = {
  left: () => 'You headed home.',
  walked_away: () => 'You walked away from the door.',
  turned_away: (host) => `${host} isn't receiving visitors right now 🚪`,
  sent_home: (host) => `${host} said it's time to go. See you next time 👋`,
  host_out: (host) => `${host} headed out, so you went home 👋`,
  no_answer: (host) => `No answer at ${host}'s door. Maybe they're busy 🤷`,
};
